import { Injectable, signal } from '@angular/core';
import { Auth, CufdData, FormAuth, GetUser } from './auth.interface';
import { HttpClient, HttpContext } from '@angular/common/http';
import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { ValidatorsService } from '../services/validators.service';
import { NotUseJWT } from '../core/interceptors/jwt.interceptor';
import Swal from 'sweetalert2';
const base_url = environment.base_url;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  _user = signal<GetUser | undefined>(undefined);
  _cufd = signal<CufdData | null>(this.loadCufdFromLocalStorage());
  constructor(
    private http: HttpClient,
    private router: Router,
    private validatorsService: ValidatorsService
  ) { }

  get token(): string {
    return sessionStorage.getItem('token') || '';
  }

  get refreshToken(): string {
    return sessionStorage.getItem('refreshToken') || '';
  }

  get headerToken() {
    return { headers: { Authorization: this.token } };
  }

  get expireTokenIn(): number {
    const exp = sessionStorage.getItem('expireTokenIn');
    return exp ? parseInt(exp, 10) : 0;
  }

  isTokenExpired(): boolean {
    const exp = this.expireTokenIn;
    if (!exp) return true;
    const nowInSec = Math.floor(Date.now() / 1000);
    // If expired or expiring in less than 30 seconds
    return (exp - nowInSec) <= 30;
  }

  checkAuthStatus(): Observable<boolean> {
    if (this.token && !this.isTokenExpired()) {
      if (this._user()?.id) {
        // Token is valid and user profile is in memory -> Immediate local approval (0 network requests)
        return of(true);
      }
      // Token is valid but user profile not in memory (e.g. reload F5) -> fetch profile
      return this.getUser().pipe(
        catchError(() => {
          if (this.refreshToken) {
            return this.refresh();
          }
          return of(false);
        })
      );
    }
    // Token is expired or missing, but refreshToken exists -> refresh
    if (this.refreshToken) {
      return this.refresh();
    }
    return of(false);
  }

  login(formData: FormAuth): Observable<boolean> {
    return this.http.post<Auth>(`${base_url}/auth`, formData,
      { context: new HttpContext().set(NotUseJWT, true) }).pipe(
        switchMap((resp: Auth) => {
          if (resp && resp.accessToken) {
            sessionStorage.setItem('token', resp.accessToken);
            sessionStorage.setItem('refreshToken', resp.refreshToken);
            if (resp.expireTokenIn) {
              sessionStorage.setItem('expireTokenIn', resp.expireTokenIn.toString());
            }
            return this.getUser();
          } else {
            return of(false);
          }
        }),
        catchError((error) => {
          return of(false);
        })
      );
  }

  refresh(): Observable<boolean> {
    return this.http
      .post<Auth>(
        `${base_url}/auth/refresh`,
        { refreshToken: this.refreshToken },
        { context: new HttpContext().set(NotUseJWT, true) }
      )
      .pipe(
        switchMap((resp: Auth) => {
          if (resp.refreshToken) {
            sessionStorage.setItem('refreshToken', resp.refreshToken);
            sessionStorage.setItem('token', resp.accessToken);
            if (resp.expireTokenIn) {
              sessionStorage.setItem('expireTokenIn', resp.expireTokenIn.toString());
            }
            return this.getUser().pipe(
              map((userResp) => {
                return userResp;
              })
            );
          } else {
            return of(false);
          }
        }),
        catchError((error) => {
          return of(false);
        })
      );
  }

  getUser(): Observable<boolean> {
    return this.http.get<GetUser>(`${base_url}/auth/me`).pipe(
      map((resp: GetUser) => {

        if (resp.company?.logo) {
          localStorage.setItem('logo', resp.company.logo);
        }

        if (resp.id) {
          if (resp.role?.name !== 'SUPER_ADMIN' && resp.company?.subscription?.accessType === 'API_ONLY') {
            sessionStorage.clear();
            this.validatorsService.resetState();
            Swal.fire({
              icon: 'warning',
              title: 'Acceso Restringido',
              text: 'Tu plan de suscripción actual es exclusivo para API. No tienes permisos para acceder a la plataforma Web.',
              confirmButtonText: 'Entendido'
            }).then(() => {
              window.location.href = '/auth';
            });
            return false;
          }

          this._user.set(resp);
          this.validatorsService.user.set(resp);
          this.validatorsService.company.set(resp.company);
          this.validatorsService.listBranch.set(resp.listBranch);
          return true;
        } else {
          return false;
        }
      }),
      catchError((error) => {
        return of(false);
      })
    );
  }

  logout() {
    const saveUsername = localStorage.getItem('saveUsername');
    const username = localStorage.getItem('username');
    const themeMode = localStorage.getItem('themeMode');
    const typePrint = localStorage.getItem('typePrint');

    // Backup tour keys
    const tourKeys: { [key: string]: string } = {};
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('tour_')) {
        tourKeys[key] = localStorage.getItem(key)!;
      }
    }

    localStorage.clear();
    sessionStorage.clear();

    if (saveUsername) {
      localStorage.setItem('username', username!);
      localStorage.setItem('saveUsername', saveUsername!);
    }
    if (themeMode) {
      localStorage.setItem('themeMode', themeMode!);
    }
    if (typePrint) {
      localStorage.setItem('typePrint', typePrint);
    }

    // Restore tour keys
    Object.keys(tourKeys).forEach(key => {
      localStorage.setItem(key, tourKeys[key]);
    });

    // Reset memory state and signals
    this._user.set(undefined);
    this._cufd.set(null);
    this.validatorsService.resetState();
    document.body.setAttribute('data-bs-theme', 'light');

    // Purge browser/ServiceWorker caches
    if ('caches' in window) {
      caches.keys().then((names) => {
        names.forEach((name) => {
          caches.delete(name);
        });
      });
    }

    window.location.href = '/auth';
  }

  // === CUFD MANAGEMENT ===
  private loadCufdFromLocalStorage(): CufdData | null {
    const local = localStorage.getItem('dataCufd');
    if (local) {
      try {
        return JSON.parse(local);
      } catch (error) {
        console.error('Error parsing CUFD from localStorage', error);
      }
    }
    return null;
  }

  updateCufd(data: CufdData): void {
    localStorage.setItem('dataCufd', JSON.stringify(data));
    this._cufd.set(data);
  }

  refreshCufd(): void {
    const updated = this.loadCufdFromLocalStorage();
    this._cufd.set(updated);
  }
}
